import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Alert, AlertDescription } from "@opencloud/ui";
import { Workspace } from "./Workspace.js";
import type { ComposerRuntime } from "./ComposerRuntime.js";
import { explicitHarness, useConversation, type ChatSnapshot } from "../use-conversation.js";
import type { AgentAppView, AgentModelProvider, AgentRuntimeChoice, AiModelChoice } from "../types.js";
import { choiceKey, composerRuntimeView, runtimeChoiceToSend, settledRuntimeChoice, useAgentModelPicker } from "../agent-models.js";
import { aiProvidersUrl, appAiDashboardUrl } from "../app-ai.js";
import { useAppAutomaticStatus } from "../automatic-failures.js";
import { personalAiAccountsAllowed } from "../bootstrap.js";
import { acceptsModelChoice, modelChoiceProvider, nonDefaultEngine, openCodeHarnessForProvider } from "../harness-provider.js";
import { useChatUploads } from "../use-chat-uploads.js";
import type { AgentEventStream } from "../use-agent-events.js";
import { updateConversationSidebar } from "../conversations-api.js";
import { api, browserRequestId } from "../api.js";
import { CONNECT_AI_FAILURE_CODES } from "./Transcript.js";
import { SharedAppsNotice, sharedAgentApps } from "./SharedAppsNotice.js";

type WorkspaceProps = ComponentProps<typeof Workspace>;
export type ConversationChatProps = Pick<WorkspaceProps,
  "bootstrap" | "session" | "apps" | "workingAppIds" | "attentionAppIds" | "threadsByApp" |
  "draft" | "onDraft" | "onHome" | "onNewApp" | "onSelectApp" | "onAgentSettings" | "offline"> & Partial<WorkspaceProps> & {
  conversationId: string | null;
  app?: AgentAppView | null;
  /** Whether a new conversation's harness takes a model choice, so its composer offers one. */
  newConversationModels?: boolean;
  /** The model provider a new conversation's harness will require. */
  newConversationProvider?: AgentModelProvider | null;
  /** The installation default harness, to name an engine that differs from it. */
  defaultHarnessVersion?: string;
  eventStream: AgentEventStream;
  appControls?: Partial<WorkspaceProps>;
  onAttached?: (app: AgentAppView, threadId: string) => void;
  onSnapshot?: (snapshot: ChatSnapshot | null) => void;
  onSubmitted?: () => void;
  onSelected?: (id: string | null, appId: string | null) => void;
  /** A question above the composer, such as the app's AI prompts; only for people who can send. */
  contextNotice?: ReactNode;
};

export function ConversationChat(props: ConversationChatProps) {
  const selectedId = props.conversationId;
  const drafts = useRef(new Map<string | null, string>());
  const readOnOpen = useRef<string | null>(null);
  const manuallyUnread = useRef(new Set<string>());
  const draftView = useRef({ id: selectedId, text: props.draft });
  useEffect(() => {
    if (draftView.current.id !== selectedId) {
      drafts.current.set(draftView.current.id, draftView.current.text);
      const text = drafts.current.get(selectedId) ?? "";
      draftView.current = { id: selectedId, text };
      props.onDraft(text);
    } else draftView.current.text = props.draft;
  }, [selectedId, props.draft, props.onDraft]);
  // Which AI account pays is the server's decision: the app's one AI setting,
  // or the person's defaults for a private chat. Nothing is chosen here.
  const chat = useConversation({ id: selectedId,
    appId: props.app?.id ?? null,
    events: props.eventStream,
    onCreated: id => {
      draftView.current.id = id;
      props.onSelected?.(id, props.app?.id ?? null); history.replaceState(history.state, "", `/?conversation=${id}`);
    }, onSent: (id, files) => uploads.acknowledge(files, `/v1/conversations/${id}`) });
  const uploads = useChatUploads(`conversation:${chat.viewKey}`,
    chat.id ? `/v1/conversations/${chat.id}` : null,
    async () => `/v1/conversations/${await chat.ensureConversation(props.draft)}`);
  const attached = useRef<string | null>(null);
  // Selection and the subscription reset settle in separate effects. Never
  // attach the previous snapshot's app while navigating to another chat/home.
  const snapshot = chat.snapshot?.thread.id === selectedId
    ? chat.snapshot : null;
  const app = chat.denied ? null : snapshot ? snapshot.app : props.app ?? null;
  useEffect(() => { if (chat.denied) props.onDraft(""); }, [chat.denied, props.onDraft]);
  useEffect(() => props.onSnapshot?.(snapshot), [snapshot, props.onSnapshot]);
  useEffect(() => {
    if (!snapshot) return;
    let active = true;
    const markOpenConversationRead = () => {
      if (document.visibilityState !== "visible") return;
      const firstReadInView = readOnOpen.current !== snapshot.conversation.id;
      if (firstReadInView) {
        readOnOpen.current = snapshot.conversation.id;
        manuallyUnread.current.delete(snapshot.conversation.id);
      }
      if (!snapshot.conversation.unread) return;
      if (!firstReadInView && manuallyUnread.current.has(snapshot.conversation.id)) return;
      void updateConversationSidebar(snapshot.conversation.id, { unread: false })
        .then(result => {
          if (active) {
            manuallyUnread.current.delete(snapshot.conversation.id);
            props.eventStream.upsertConversation(result.conversation);
          }
        })
        .catch(() => undefined);
    };
    markOpenConversationRead();
    document.addEventListener("visibilitychange", markOpenConversationRead);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", markOpenConversationRead);
    };
  }, [snapshot?.conversation.id, snapshot?.conversation.unread, props.eventStream.upsertConversation]);
  useEffect(() => {
    if (!app) { attached.current = null; return; }
    if (snapshot && app.id !== props.app?.id && attached.current !== app.id) {
      attached.current = app.id;
      props.eventStream.refresh();
      props.onAttached?.(app, snapshot.thread.id);
    }
  }, [app?.id, snapshot?.thread.id, props.app?.id, props.eventStream.refresh]);
  // Unsent model and reasoning choices, and the open picker, belong to this chat view only.
  const [runtimeChoice, setRuntimeChoice] = useState<{ view: number; choice: AgentRuntimeChoice }>(
    () => ({ view: chat.viewKey, choice: {} }));
  const choice = runtimeChoice.view === chat.viewKey ? runtimeChoice.choice : {};
  const chooseRuntime = (patch: AgentRuntimeChoice) => setRuntimeChoice(current => ({ view: chat.viewKey,
    choice: { ...(current.view === chat.viewKey ? current.choice : {}), ...patch } }));
  const [pickerView, setPickerView] = useState<number | null>(null);
  const thread = snapshot?.thread ?? null;
  // Personal app admins choose whose provider pays for their own messages.
  const modelChoice = (snapshot?.participation?.role !== "builder" || personalAiAccountsAllowed(props.bootstrap)) &&
    (thread ? acceptsModelChoice(thread.harnessVersion) : !selectedId && props.newConversationModels === true);
  const models = useAgentModelPicker({ appId: app?.id ?? null, threadId: thread?.id ?? null,
    enabled: modelChoice, retryKey: props.offline });
  // A new chat runs the harness this tab named, if any, on the provider of its pick.
  const pickedView = composerRuntimeView({ picker: models.picker, thread, choice });
  const newChatProvider = modelChoiceProvider(pickedView?.choice?.model) ?? props.newConversationProvider;
  const newChatHarness = !selectedId && newChatProvider ? explicitHarness() : null;
  const engine = props.defaultHarnessVersion
    ? nonDefaultEngine(thread?.harnessVersion
      ?? (newChatHarness ? openCodeHarnessForProvider(newChatHarness, newChatProvider) : null),
      props.defaultHarnessVersion)
    : null;
  const runtimeView = pickedView && { ...pickedView, engineLabel: engine?.label ?? null, engineFullName: engine?.fullName ?? null };
  const currentChoiceKey = runtimeView?.currentChoice ? choiceKey(runtimeView.currentChoice) : null;
  useEffect(() => {
    if (runtimeView) setRuntimeChoice(current => current.view === chat.viewKey
      ? { ...current, choice: settledRuntimeChoice(current.choice, thread, runtimeView) } : current);
  }, [currentChoiceKey, thread?.reasoningEffort, chat.viewKey]);
  // Error cards read the app's automatic choice, which follows the chat's latest saved pick.
  const failureSignature = snapshot?.messages.flatMap(message => message.role === "system" && message.automaticFailure
    ? [`${message.id}:${message.automaticFailure.kind === "features" ? message.automaticFailure.lastAt : ""}`] : [])
    .join(",") ?? "";
  const automatic = useAppAutomaticStatus(app && failureSignature ? app.id : null,
    `${failureSignature}|${thread?.model ?? ""}`);
  // A saved pick changes the thread's choice and the app's automatic tasks; a
  // refused one shows why on its model.
  const pendingDelivery = chat.pending ? `${chat.pending.requestId}:${chat.pending.delivery}` : null;
  useEffect(() => {
    const pending = chat.pending;
    if ((pending?.delivery === "sent" && pending.runtime?.choice) ||
      (pending?.delivery === "failed" && pending.failureCode === "ai_model_unavailable")) {
      models.refresh();
      automatic.refresh();
    }
  }, [pendingDelivery]);
  const openPicker = (open: boolean) => {
    setPickerView(open ? chat.viewKey : null);
    // Credit and limits change, so an opened picker reads them again.
    if (open) models.refresh();
  };
  const personal = personalAiAccountsAllowed(props.bootstrap);
  const directory = chat.directory;
  const organisationId = directory?.account.state === "active" ? directory.account.directory.id
    : directory?.organisations[0]?.id ?? null;
  const composerRuntime: ComposerRuntime | undefined = runtimeView ? { ...runtimeView,
    open: pickerView === chat.viewKey,
    onOpenChange: openPicker,
    onChoiceChange: (next: AiModelChoice) => chooseRuntime({ choice: next }),
    onReasoningEffortChange: (reasoningEffort: string) => chooseRuntime({ reasoningEffort }),
    appName: app ? app.name ?? "this app" : null,
    providerPages: {
      organisation: !personal,
      manageUrl: aiProvidersUrl({ appsUrl: props.bootstrap.appsUrl, personal, organisationId }),
      connectUrl: aiProvidersUrl({ appsUrl: props.bootstrap.appsUrl, personal, organisationId, connect: true }),
    } } : undefined;
  const automaticFailures = app ? {
    appName: app.name ?? "this app",
    status: automatic.status,
    appAiUrl: appAiDashboardUrl(props.bootstrap.appsUrl, app.id),
    onPickModel: composerRuntime && chat.canSend ? () => openPicker(true) : undefined,
  } : undefined;
  // An answer continues the waiting root; the choice waits for the next message.
  const nextRuntime = () => runtimeView && !snapshot?.question ? runtimeChoiceToSend(thread, choice, runtimeView) : undefined;
  const send = () => {
    if (!chat.canSend || chat.busy || props.offline || uploads.pending || (!props.draft.trim() && !uploads.ready.length)) return;
    const value = props.draft, files = uploads.ready;
    const runtime = nextRuntime();
    uploads.clear(true); props.onDraft(""); props.onSubmitted?.(); void chat.send(value, undefined, files, runtime);
  };
  // A message refused for its model is resent with the pick made since.
  const retryMessage = () => {
    const pending = chat.pending;
    if (!pending) return;
    void chat.send(pending.content, pending.kind === "message" && runtimeView
      ? { ...pending, runtime: nextRuntime() } : pending);
  };
  // An app chat never inherits personal-connection use; the owner grants it
  // explicitly here with the same delegation the Agent settings page records.
  const connectionId = snapshot?.thread.providerConnectionId ?? null;
  const [allowError, setAllowError] = useState<string | null>(null);
  const allowProvider = app && snapshot && connectionId && chat.pending?.failureCode === "provider_delegation_required"
    ? () => { void (async () => {
        const pending = chat.pending;
        try {
          await api(`/v1/apps/${encodeURIComponent(app.id)}/provider-delegations`, { method: "POST",
            body: JSON.stringify({ requestId: browserRequestId(), providerConnectionId: connectionId, threadId: snapshot.thread.id }) });
        } catch (failure) {
          setAllowError(failure instanceof Error ? failure.message : "Permission could not be saved. Try again.");
          return;
        }
        setAllowError(null);
        if (pending) await chat.send(pending.content, pending);
      })(); }
    : undefined;
  return <Workspace
    incidents={[]} releases={[]} focusMessageId={null}
    onReleaseHistory={() => {}} onLoadRelease={async () => { throw new Error("No app release selected"); }}
    onReportProblem={async () => {}}
    {...props} {...props.appControls}
    {...(props.eventStream.ready ? { conversations: props.eventStream.conversations } : {})}
    workingAppIds={props.eventStream.ready ? props.eventStream.workingAppIds : props.workingAppIds}
    attentionAppIds={props.eventStream.ready ? props.eventStream.attentionAppIds : props.attentionAppIds ?? new Set()}
    workingConversationIds={props.eventStream.workingConversationIds}
    attentionConversationIds={props.eventStream.attentionConversationIds}
    conversationPreviews={props.eventStream.latestMessages}
    conversationActivity={props.eventStream.conversationActivity}
    loadingConversation={Boolean(selectedId && !snapshot && !chat.denied)}
    app={app} thread={snapshot?.thread ?? null} snapshot={snapshot}
    uploads={uploads} composerRuntime={composerRuntime} automaticFailures={automaticFailures}
    conversation={snapshot?.conversation ?? null} canSend={Boolean(chat.canSend)}
    pendingMessage={chat.pending} awaitingReply={Boolean(chat.pending && chat.pending.delivery !== "failed")}
    liveText={chat.liveText} busy={chat.busy} stale={chat.stale} stopping={chat.stopping}
    onSend={send} onStop={() => void chat.stop()} onAnswer={value => void chat.send(value)}
    onRetry={runId => void chat.retry(runId, "run")}
    onRetryAgentMessage={messageId => chat.retry(messageId, "message")}
    onRetryMessage={retryMessage}
    composerNote={chat.canSend ? props.composerNote : null}
    onAllowProvider={allowProvider}
    onConnectProvider={chat.pending?.delivery === "failed" && CONNECT_AI_FAILURE_CODES.has(chat.pending.failureCode ?? "") ? props.onAgentSettings : undefined}
    onSelectConversation={record => {
      props.onSelected?.(record.id, record.appId);
      history.pushState(history.state, "", `/?conversation=${record.id}`);
    }}
    onConversationChanged={(conversationId, conversation, change) => {
      if (change.unread === true) manuallyUnread.current.add(conversationId);
      if (change.unread === false) manuallyUnread.current.delete(conversationId);
      if (conversation) props.eventStream.upsertConversation(conversation);
      else {
        props.eventStream.removeConversation(conversationId);
        if (selectedId === conversationId) props.onHome();
      }
    }}
    onHome={props.onHome}
    onNewApp={props.onNewApp}
    contextControls={<>
      {chat.directory && !["personal", "active"].includes(chat.directory.account.state) ? <Alert><AlertDescription>
        {chat.directory.account.state === "onboarding_required" ? "Create your organisation, or accept your company’s invitation, before starting new work." : chat.directory.account.state === "removed" ? "Your company membership has been removed. Ask a company administrator for a new invitation." : "Your company is suspended. New work is unavailable."}{" "}
        <a href={`${new URL(props.bootstrap.appsUrl, location.href).origin}/organisations`}>{chat.directory.account.state === "onboarding_required" ? "Set up organisation" : "View company status"}</a>
      </AlertDescription></Alert> : null}
      {app ? null : <SharedAppsNotice apps={sharedAgentApps(props.session, props.apps)} onSelectApp={(shared) => props.onSelectApp(shared)} />}
      {chat.error ? <Alert variant="destructive"><AlertDescription>{chat.error}</AlertDescription></Alert> : null}
      {allowError ? <Alert variant="destructive"><AlertDescription>{allowError}</AlertDescription></Alert> : null}
      {chat.canSend ? props.contextNotice : null}
    </>}
  />;
}
